/**
 * The river house's bowed upper balcony (lifted from lantern-water's
 * `river-balcony.js`): a curved limestone slab, four curved rails, iron
 * balusters with brass finials and scrolls, and shaped limestone brackets
 * kept above the porch's head space. Built at (x, front) in `parent`'s space;
 * every finish is a shared town material.
 */
import * as THREE from 'three';
import type { Ctx } from './kit.ts';

export const riverBalcony = (ctx: Ctx, parent: THREE.Object3D, x: number, front: number): THREE.Group => {
  const m = ctx.m;
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const p = new THREE.Group();
  p.name = 'river-house-bowed-balcony';
  p.position.set(x, 0, front);
  parent.add(p);
  const edge = (angle: number, y: number): THREE.Vector3 => new THREE.Vector3(1.16 * Math.cos(angle), y, 0.18 + 0.72 * Math.sin(angle));
  const plan = new THREE.Shape();
  plan.moveTo(-1.16, 0.18);
  plan.lineTo(1.16, 0.18);
  for (let i = 1; i <= 24; i += 1) {
    const q = edge((i * Math.PI) / 24, 0);
    plan.lineTo(q.x, q.z);
  }
  plan.closePath();
  const slab = new THREE.ExtrudeGeometry(plan, { depth: 0.14, bevelEnabled: false, steps: 1, curveSegments: 1 });
  slab.rotateX(Math.PI / 2);
  mesh(p, slab, m.cream, 0, 2.62, 0);
  const curvedRail = (y: number, radius: number, material: THREE.Material): THREE.Mesh => {
    const points = Array.from({ length: 25 }, (_, i) => edge((i * Math.PI) / 24, y));
    return mesh(p, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 32, radius, 5, false), material, 0, 0, 0);
  };
  curvedRail(2.52, 0.035, m.stone);
  curvedRail(2.64, 0.025, m.cream);
  curvedRail(2.79, 0.026, m.iron);
  curvedRail(3.48, 0.034, m.iron);
  const scroll = new THREE.TorusGeometry(0.12, 0.013, 4, 16, Math.PI * 1.65);
  for (let i = 0; i <= 10; i += 1) {
    const angle = (i * Math.PI) / 10;
    const q = edge(angle, 0);
    line(p, [q.x, 2.67, q.z], [q.x, 3.49, q.z], 0.021, m.iron);
    if (i % 5 === 0) mesh(p, new THREE.SphereGeometry(0.043, 6, 4), m.brass, q.x, 3.53, q.z, 1, 1.35, 1);
    if (i < 10) {
      const a = ((i + 0.5) * Math.PI) / 10;
      const s = edge(a, 3.095);
      const o = mesh(p, scroll, m.iron, s.x, s.y, s.z);
      o.rotation.y = Math.atan2(0.72 * Math.cos(a), 1.16 * Math.sin(a));
      o.rotation.z = i % 2 ? 0.4 : -0.4;
    }
  }
  const profile = new THREE.Shape();
  profile.moveTo(0, 0);
  profile.lineTo(0.46, 0);
  profile.lineTo(0.46, -0.08);
  profile.bezierCurveTo(0.29, -0.07, 0.37, -0.28, 0.24, -0.26);
  profile.bezierCurveTo(0.14, -0.25, 0.22, -0.4, 0.07, -0.44);
  profile.lineTo(0, -0.44);
  profile.closePath();
  const support = new THREE.ExtrudeGeometry(profile, { depth: 0.17, bevelEnabled: true, bevelSize: 0.012, bevelThickness: 0.012, bevelSegments: 1, curveSegments: 5 });
  support.rotateY(-Math.PI / 2);
  for (const side of [-1, 1]) {
    mesh(p, support, m.cream, side * 0.98 + 0.085, 2.5, 0.16);
    box(p, side * 0.98, 2.28, 0.16, 0.21, 0.48, 0.14, m.stone);
    box(p, side * 0.98, 2.49, 0.35, 0.28, 0.09, 0.48, m.cream);
  }
  return p;
};
